import React, { useEffect, useMemo, useRef, useState } from "react";
import { OperatorEmblem } from "./OperatorEmblem";

/**
 * Spinning wheel of operators.
 */
const SLICES = 16;
const SLICE_DEG = 360 / SLICES;
const SPIN_MS = 3500;

export const OperatorWheel = ({ pool, finalPick, side = "attacker", spinKey, onSpinEnd }) => {
  const accent = side === "attacker" ? "#E67300" : "#1F88E8";
  const altAccent = side === "attacker" ? "#3a1d0a" : "#0c2a45";

  // Build a fresh slot arrangement whenever spinKey changes.
  const slots = useMemo(() => {
    if (!finalPick) return [];
    const others = (pool || []).filter((o) => o.id !== finalPick.id);
    const shuffled = [...others].sort(() => Math.random() - 0.5);
    const filler = shuffled.slice(0, SLICES - 1);
    while (filler.length < SLICES - 1 && others.length > 0) {
      filler.push(others[Math.floor(Math.random() * others.length)]);
    }
    return [finalPick, ...filler];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spinKey]);

  // Rotation target: 5 full spins + small jitter inside the winning slice.
  const rotation = useMemo(() => {
    if (!finalPick) return 0;
    const jitter = (Math.random() - 0.5) * SLICE_DEG * 0.55;
    return -(5 * 360 + jitter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spinKey]);

  // Measure actual rendered wheel size so icon offsets are reliable.
  const wheelRef = useRef(null);
  const [wheelSize, setWheelSize] = useState(0);
  useEffect(() => {
    const update = () => {
      if (wheelRef.current) setWheelSize(wheelRef.current.offsetWidth);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  // Kick off the spin animation once we have the target.
  const [currentRotation, setCurrentRotation] = useState(0);
  const endedRef = useRef(false);
  useEffect(() => {
    endedRef.current = false;
    setCurrentRotation(0);
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => setCurrentRotation(rotation));
    });
    return () => cancelAnimationFrame(id);
  }, [rotation]);

  const handleTransitionEnd = (e) => {
    // Ignore bubbling transitions from child elements (e.g., hover states).
    if (e.propertyName !== "transform") return;
    if (endedRef.current) return;
    endedRef.current = true;
    if (onSpinEnd) onSpinEnd();
  };

  const iconOffset = wheelSize * 0.36; // how far from centre to place icons (px)

  return (
    <div
      data-testid="operator-wheel"
      className="relative w-full flex items-center justify-center"
      style={{ minHeight: "min(86vw, 460px)" }}
    >
      {/* Pointer / indicator at top */}
      <div
        aria-hidden="true"
        className="absolute left-1/2 -translate-x-1/2 z-20"
        style={{
          top: -6,
          width: 0,
          height: 0,
          borderLeft: "14px solid transparent",
          borderRight: "14px solid transparent",
          borderTop: `22px solid ${accent}`,
          filter: `drop-shadow(0 0 6px ${accent}aa)`,
        }}
      />
      {/* Wheel container (measured) */}
      <div
        ref={wheelRef}
        className="relative"
        style={{
          width: "min(86vw, 460px)",
          height: "min(86vw, 460px)",
          marginTop: "16px",
        }}
      >
        {/* Rotating disc */}
        <div
          className="absolute inset-0 rounded-full"
          style={{
            transform: `rotate(${currentRotation}deg)`,
            transition: `transform ${SPIN_MS}ms cubic-bezier(0.17, 0.67, 0.12, 0.99)`,
            background: `radial-gradient(circle at center, ${altAccent}88 0%, #0a0a0a 70%)`,
            boxShadow: `0 0 0 2px ${accent}55, 0 0 60px -10px ${accent}44, inset 0 0 30px #000`,
            willChange: "transform",
          }}
          onTransitionEnd={handleTransitionEnd}
        >
          {/* Slice dividers */}
          {slots.map((_, i) => (
            <div
              key={`div-${i}`}
              aria-hidden="true"
              className="absolute left-1/2"
              style={{
                top: 0,
                width: 1,
                height: "50%",
                background: `${accent}22`,
                transform: `translateX(-50%) rotate(${i * SLICE_DEG}deg)`,
                transformOrigin: "bottom center",
              }}
            />
          ))}
          {/* Operator icons — only rendered once the wheel size is known */}
          {wheelSize > 0 &&
            slots.map((op, i) => {
              const angle = i * SLICE_DEG;
              return (
                <div
                  key={`${op.id}-${i}`}
                  className="absolute"
                  style={{
                    top: "50%",
                    left: "50%",
                    width: 54,
                    height: 54,
                    marginLeft: -27,
                    marginTop: -27,
                    // Rotate around centre, translate outward, counter-rotate so icon stays upright.
                    transform: `rotate(${angle}deg) translateY(${-iconOffset}px) rotate(${-angle}deg)`,
                  }}
                >
                  <OperatorEmblem id={op.id} name={op.name} side={side} size={54} />
                </div>
              );
            })}
        </div>

        {/* Hub / centre disc */}
        <div
          aria-hidden="true"
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full flex items-center justify-center"
          style={{
            width: 72,
            height: 72,
            background: "#0a0a0a",
            boxShadow: `0 0 0 2px ${accent}`,
          }}
        >
          <div
            className="text-[10px] tracking-[0.3em] uppercase font-bold"
            style={{ color: accent, fontFamily: '"JetBrains Mono", monospace' }}
          >
            Spin
          </div>
        </div>
      </div>
    </div>
  );
};

export default OperatorWheel;
